// Client for the RIC conversation store in Phoenix (Phoenix.Ric.Api).
//
// Phoenix rather than Sphinx on purpose. Sphinx decodes bearer tokens without verifying their
// signatures, so it cannot be the place that decides who may read whose transcript; Phoenix
// validates properly. See the note on RicConversationController.
//
// Written as JS interop rather than a typed HttpClient because Ra.Ui has no HttpClient of its own
// and reads every base URL from window.riskalive already — the same way ra.chatStream does.

const base = () => {
    try {
        return (window.riskalive && window.riskalive.urls && window.riskalive.urls.phoenix) || '';
    } catch {
        return '';
    }
};

const token = async () => {
    if (!window.riskalive || !window.riskalive.sso) return null;
    return window.riskalive.sso.getToken();
};

/**
 * One request against the conversation store.
 *
 * Returns a discriminated result rather than throwing. Persistence here is a convenience layered
 * over a chat that still works without it: a save that fails must not take down the conversation
 * the user is in the middle of, and the caller needs to be able to tell "not found" from "wrong
 * company" from "the network is down" to say anything useful.
 */
const send = async (method, path, body) => {
    const root = base();
    if (!root) {
        return { ok: false, status: 0, error: 'No Phoenix URL configured.' };
    }

    try {
        const headers = { 'Accept': 'application/json' };
        const bearer = await token();
        if (bearer) headers['Authorization'] = 'Bearer ' + bearer;
        if (body !== undefined) headers['Content-Type'] = 'application/json';

        const resp = await fetch(root + path, {
            method,
            headers,
            body: body === undefined ? undefined : JSON.stringify(body),
        });

        if (resp.status === 204) {
            return { ok: true, status: 204, data: null };
        }

        const text = await resp.text();
        let parsed = null;
        if (text) {
            try { parsed = JSON.parse(text); } catch { /* not json; leave null */ }
        }

        if (!resp.ok) {
            // The server sends { error, wrongCompany } when a share belongs to another company,
            // which is the one failure worth explaining rather than swallowing.
            let message = (parsed && parsed.error) || resp.statusText;

            // Outside Production a save failure also carries the exception that caused it. Fold it
            // into the message rather than dropping it: the caller logs this string, and without it
            // a 500 reads as "could not save" with the reason sitting unread in a response body.
            if (parsed && parsed.detail) {
                message += ' — ' + parsed.detail;
                if (parsed.inner) message += ' (' + parsed.inner + ')';
            }

            return {
                ok: false,
                status: resp.status,
                error: message,
                wrongCompany: !!(parsed && parsed.wrongCompany),
                externalSharingDisabled: !!(parsed && parsed.externalSharingDisabled),
            };
        }

        return { ok: true, status: resp.status, data: parsed };
    } catch (e) {
        return { ok: false, status: 0, error: (e && e.message) || String(e) };
    }
};

// Results come back as JSON strings: Blazor's interop deserialises into the caller's own types, and
// handing it a string keeps the DTO shape defined in one place (C#) rather than two.
const wrap = async (promise) => JSON.stringify(await promise);

export default {
    list: () => wrap(send('GET', '/api/ric/conversations')),
    get: (id) => wrap(send('GET', `/api/ric/conversations/${encodeURIComponent(id)}`)),
    save: (id, payload) => wrap(send('PUT', `/api/ric/conversations/${encodeURIComponent(id)}`, payload)),
    remove: (id) => wrap(send('DELETE', `/api/ric/conversations/${encodeURIComponent(id)}`)),
    // The links a conversation already has, so the share panel can show one again after a reload.
    shares: (id) => wrap(send('GET', `/api/ric/conversations/${encodeURIComponent(id)}/shares`)),

    share: (id) => wrap(send('POST', `/api/ric/conversations/${encodeURIComponent(id)}/share`)),
    unshare: (id) => wrap(send('DELETE', `/api/ric/conversations/${encodeURIComponent(id)}/share`)),
    unpublish: (id) => wrap(send('DELETE', `/api/ric/conversations/${encodeURIComponent(id)}/share/external`)),
    getShared: (shareToken) => wrap(send('GET', `/api/ric/shared/${encodeURIComponent(shareToken)}`)),
    shareExternal: (id) => wrap(send('POST', `/api/ric/conversations/${encodeURIComponent(id)}/share/external`)),
    companySharing: () => wrap(send('GET', '/api/ric/company-sharing')),
    fork: (shareToken) => wrap(send('POST', `/api/ric/shared/${encodeURIComponent(shareToken)}/fork`)),

    // The published-link equivalent. Separate endpoint because it answers a different question:
    // that one is company-gated, this one accepts a link anyone can open and lands the copy in the
    // CALLER's company rather than the origin's.
    forkPublic: (shareToken) => wrap(send('POST', `/api/ric/public/${encodeURIComponent(shareToken)}/fork`)),
};
